// eslint-disable-next-line import/order
import { colors, fontSizeSmall } from '@atlaskit/theme';

import { gridSize, borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import type {
	HtmlString,
	TooltipFormatter,
	BarChartTooltipFormatterParams,
} from '../../../../common/types';
import { useFormatDurationFromSeconds } from '../../../../common/utils/date-range';
import { upIcon, downIcon, issuesIcon } from './assets/icons';
import messages from './messages';

type IconInfo = {
	style: string;
	iconHtml: HtmlString;
};
const getIconInfo = (value: number, cycleTimeStatisticsInSeconds: number | null): IconInfo => {
	let iconHtml = '';
	let style = '';
	if (cycleTimeStatisticsInSeconds === null) {
		return { style, iconHtml };
	}

	if (value < cycleTimeStatisticsInSeconds) {
		iconHtml = downIcon;
		style = iconGood;
	}
	if (value > cycleTimeStatisticsInSeconds) {
		iconHtml = upIcon;
		style = iconWarn;
	}

	return { style, iconHtml };
};

export const useTooltipFormatter = (
	medianInSeconds: number | null,
): TooltipFormatter<BarChartTooltipFormatterParams> => {
	const { formatDate, formatMessage } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	return ({ data }: BarChartTooltipFormatterParams) => {
		const {
			customInfo: { numIssues, startDateInclusive, tooltipFormatCycleTime },
		} = data;

		const weekStartDateText = formatMessage(messages.weekStartDate, {
			date: formatDate(startDateInclusive, { day: 'numeric', month: 'short' }),
		});

		const statisticsText = formatDurationFromSeconds(tooltipFormatCycleTime, {
			days: 'short',
			hours: 'short',
		});

		const numIssuesText = formatMessage(messages.numIssueShipped, { numIssues });
		const { style: comparisonIconStyle, iconHtml } = getIconInfo(
			tooltipFormatCycleTime,
			medianInSeconds,
		);

		return `
            <div style="${container}">
                <div style="${header}">
                    ${weekStartDateText}
                 </div>
                 <div style="${cycleTimeTextContainer}${
										iconHtml !== '' ? cycleTimeIconPositioner : ''
									}">
                    <span style="${comparisonIconStyle}">${iconHtml}</span>
                    ${statisticsText}
                </div>
                <div style="${issuesShippedContainer}">
                    <span style="${issuesIconStyled}">${issuesIcon}</span>
                    ${numIssuesText}
                </div>
            </div>
        `;
	};
};

const container = `
    height: ${gridSize * 9}px;
    padding: 0 ${gridSize / 2}px;
    background-color: ${colors.N900};
    border-radius: ${borderRadius};
    font-size: ${fontSizeSmall()}px;
    color: ${colors.N0};
`;

const header = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
    margin-bottom: ${gridSize}px;
    text-decoration-line: underline;
`;

const iconBase = `
    transform: scale(0.666);
    margin-top: 2px;
`;
const iconGood = `
    ${iconBase}
    fill: ${colors.N800};
    color: ${colors.G300}
`;
const iconWarn = `
    ${iconBase}
    fill: ${colors.N10};
    color: ${colors.Y400}
`;

const issuesIconStyled = `
    ${iconBase}
    margin-left: -5px;
`;

const cycleTimeTextContainer = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
    margin-bottom: ${gridSize}px;
`;

const cycleTimeIconPositioner = `
    margin-left: -5px;
`;

const issuesShippedContainer = `
    display: flex;
    align-items: center;
    height: ${gridSize * 2}px;
`;
